/**
 * The sections of Paramètres (/ParallelDialer/settings?section=<id>), in two
 * groups (F6, DESIGN.md §5):
 *
 *   account       what belongs to the person signed in (language, audio,
 *                 password): it follows them, whatever the organisation.
 *   organisation  what the whole organisation shares (numbers, inbound
 *                 calls, integrations, AI prompts, blacklist, API keys,
 *                 webhooks).
 *
 * One catalogue for the page and both menus (XDialSidebar, DialerRail): the
 * order here is the order on screen. Each section has its title in
 * settings:sections.<id> and one sentence in settings:sectionDescriptions.<id>.
 *
 * `coaching` was its own section until W4: it is part of « IA » now, and the
 * old address keeps working (the Outils page links to ?section=ai).
 */

export type SettingsGroupId = 'account' | 'organisation';

export interface SettingsSectionEntry {
  id: string;
  group: SettingsGroupId;
}

export const SETTINGS_SECTIONS: readonly SettingsSectionEntry[] = [
  { id: 'general', group: 'account' },
  { id: 'numbers', group: 'organisation' },
  { id: 'incoming', group: 'organisation' },
  { id: 'integrations', group: 'organisation' },
  { id: 'ai', group: 'organisation' },
  { id: 'blacklist', group: 'organisation' },
  { id: 'apiKeys', group: 'organisation' },
  // W7 (SU-2): outgoing webhooks, next to the API keys.
  { id: 'webhooks', group: 'organisation' },
];

export const DEFAULT_SETTINGS_SECTION = 'general';

/** Old addresses that still lead somewhere. */
const ALIASES: Record<string, string> = {
  coaching: 'ai',
};

/** `api-keys`, `api_keys`, `ApiKeys` → `apikeys`: the spelling a person or a doc may use (QA-W7-15). */
const loose = (v: string) => v.toLowerCase().replace(/[-_\s]/g, '');

/**
 * The section to show for a ?section= value: an alias is followed, and the
 * kebab-case, snake_case or any-case spelling of an id is accepted
 * (?section=api-keys opens Clés d'API, QA-W7-15); an unknown value falls back
 * on Général.
 */
export function resolveSettingsSection(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_SETTINGS_SECTION;
  const id = ALIASES[raw] ?? ALIASES[loose(raw)] ?? raw;
  if (SETTINGS_SECTIONS.some((s) => s.id === id)) return id;
  return SETTINGS_SECTIONS.find((s) => loose(s.id) === loose(id))?.id ?? DEFAULT_SETTINGS_SECTION;
}

export function settingsGroupOf(id: string): SettingsGroupId {
  return SETTINGS_SECTIONS.find((s) => s.id === id)?.group ?? 'account';
}

/** The sections with a group heading before the first of each group, for the menus. */
export function settingsMenuRows(): Array<{ kind: 'group'; group: SettingsGroupId } | { kind: 'section'; entry: SettingsSectionEntry }> {
  const rows: Array<{ kind: 'group'; group: SettingsGroupId } | { kind: 'section'; entry: SettingsSectionEntry }> = [];
  let last: SettingsGroupId | null = null;
  for (const entry of SETTINGS_SECTIONS) {
    if (entry.group !== last) { rows.push({ kind: 'group', group: entry.group }); last = entry.group; }
    rows.push({ kind: 'section', entry });
  }
  return rows;
}
